import {
  useBlockProps,
  useInnerBlocksProps,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { useSelect } from '@wordpress/data';

import CubeSliderInspectorControls from './InspectorControls';

import '../style.scss';
import '../editor.scss';

/*
 * Every slide is authored in the canvas as an ordinary stack, one under the
 * next — no Embla, and deliberately no cube. The rotation needs a live Embla
 * instance to read progress from, and faking a static three-quarter view here
 * would misrepresent a face box the author cannot actually edit in perspective.
 *
 * What the canvas DOES preview is the part authors can get wrong: the face box.
 * Each slide is boxed to the chosen ratio, so content that will overflow a face
 * on the frontend visibly overflows it here too.
 */
/*
 * Four faces of starter copy — the count a cube actually has, so a freshly
 * inserted block turns through a full revolution and back to where it started.
 *
 * Text only. `brandy/slide` seeds an image when it mounts empty, which suits
 * the Slider block's card row but not a cube face: a photo turning edge-on
 * reads as a glitch, and the face box is a fixed ratio the image would fight.
 * Passing inner blocks here means the slide never mounts empty, so that
 * image-first default never fires.
 *
 * The tint is a palette slug rather than a hex so it tracks the theme, matching
 * how the pattern library sets group backgrounds. Without the Brandy theme the
 * slug resolves to nothing and faces are simply untinted — no broken styling.
 */
const STARTER_FACES = [
  {
    title: 'One idea per face',
    text: 'Give each face a single thought. The cube turns; the reader keeps up.',
  },
  {
    title: 'Short beats long',
    text: 'A face is on screen for a moment. Write for the glance, not the study.',
  },
  {
    title: 'Same box, every side',
    text: 'Every face shares one ratio, so content that overflows one overflows all four.',
  },
  {
    title: 'Then it comes round',
    text: 'Four faces and the cube is back where it began. Loop it, or let it rest.',
  },
];

const TEMPLATE = STARTER_FACES.map(({ title, text }) => [
  'brandy/slide',
  {
    backgroundColor: 'brandy-gray-3',
    // Keeps the copy off the edge of the face; a heading flush to the corner
    // reads as broken once the face is rotating.
    style: { spacing: { padding: { top: '5%', right: '6%', bottom: '5%', left: '6%' } } },
  },
  [
    ['core/heading', { level: 3, content: title, placeholder: 'Face title' }],
    ['core/paragraph', { content: text, placeholder: 'Face text' }],
  ],
]);

export default function Edit({ attributes, setAttributes, clientId }) {
  const { aspectRatio } = attributes;

  const slideCount = useSelect(
    (select) => select(blockEditorStore).getBlocks(clientId).length,
    [clientId]
  );

  const blockProps = useBlockProps({
    className: 'brandy-cube-slider is-editor-preview',
    style: { '--brandy-cube-aspect': aspectRatio || '16/9' },
  });

  const innerBlocksProps = useInnerBlocksProps(
    { className: 'brandy-cube-slider__faces' },
    { template: TEMPLATE, allowedBlocks: ['brandy/slide'] }
  );

  return (
    <>
      <CubeSliderInspectorControls
        attributes={attributes}
        setAttributes={setAttributes}
      />
      <div {...blockProps}>
        <div {...innerBlocksProps} />
        {/*
          A cube has four faces and the geometry wraps every four, so slide 5
          shares a face with slide 1. Nothing overlapping is ever painted —
          only the active face and its two neighbours are visible — but the
          author should know the cube stops being a tour of distinct sides.
        */}
        {slideCount > 4 && (
          <p className="brandy-cube-slider__notice">
            {`${slideCount} slides — a cube has 4 faces, so slides beyond the fourth reuse one. They still display in order.`}
          </p>
        )}
      </div>
    </>
  );
}
